/**
 * Authors: mariushirjanu, koenweyn
 * Created: 03/06/2021
 */
import { Component, Input, SimpleChanges, OnChanges, ViewEncapsulation } from '@angular/core';
import { AuthService } from '../services/auth/auth.service';
import { FaqDataService } from './faq-data.service';
import { IFaq, IObject } from '@type_definitions/datatypes';
import { util } from '../util';
import { IFaqItem } from '@app/main/faq/list/faq-list.component';
import { STATE_FAQ, STATE_FAQ_CREATE, STATE_FAQ_EDIT } from '../app-states';

import { TranslationService } from '../translation/translation.service';
import { GaMessageService } from '../message/ga-message.service';
import { FormattingService } from '../services/formatting.service';
import { SendMailService } from '../send-mail/send-mail.service';
import { IMailRequest } from '../send-mail/send-mail-data.service';
import { SEC_SEND_FAQ } from '../services/auth/security-objects';

@Component({
  selector: 'faq-item',
  template:
    `
      <div class="faq-item">
        <h2 *ngIf="loading">
          <activity-indicator></activity-indicator>
        </h2>

        <div *ngIf="!loading && !collapsible">
          <h1 *ngIf="!noQuestion" class="faq-question">{{ faq.question }}</h1>
          <div [class.col-space-bottom]="faq.userList || faq.company" *ngIf="acslUser && onFaqPage">
            <p class="text-muted" *ngIf="faq.userList"><strong>Display for:</strong> {{ faq.userList }}</p>
            <p class="text-muted" *ngIf="faq.company"><strong>Only for company:</strong> {{ faq.company | format: 'formatCompany' }}</p>
            <p class="text-muted" *ngIf="faq.vendor"><strong>Only for vendor:</strong> {{ faq.vendor | format: 'formatCompany' }}</p>
            <p class="text-muted" *ngIf="faq.projectType"><strong>Only for webshop type:</strong> {{ faq.projectType | format: 'formatLov' }}</p>
          </div>

          <p>
            <span class="faq-answer" [innerHTML]="faq.answer | safe: 'html'"></span>
            <span *ngIf="noConfluenceText && faq.confluencePageHtml">
              <br/><a uiSref="${STATE_FAQ}" [uiParams]="{key: faq.id}" target="_blank" translate>GA.GENERAL.SEE_MORE</a>
            </span>
          </p>

          <div class="faq-edit-link">
            <a href class="btn btn-default" [uiSref]="faqAddEditSref" [uiParams]="faqAddEditSrefParams" [target]="!onFaqPage ? '_blank' : '_self'" *ngIf="acslUser"><i class="fa fa-pencil"></i> Edit</a>
            <button class="btn btn-default margin-left-sm" (click)="sendMail()" *ngIf="onFaqPage && hasPermissionToSendFAQ">
              <i class="fa fa-envelope-o"></i>&nbsp;<translate-label key="GA.BUTTONS.SEND_LABEL" label="GA.GENERAL.FAQ"></translate-label>
            </button>
          </div>

          <div *ngFor="let attachment of faq.attachments; trackById">
            <i class="fa fa-paperclip"></i>&nbsp;<a [href]="attachment.value" target="_blank">{{ attachment.id }}</a>
          </div>

          <div class="confluence" *ngIf="!noConfluenceText && faq.confluencePageHtml">
            <div [innerHTML]="faq.confluencePageHtml"></div>
          </div>
        </div>

        <div *ngIf="!loading && collapsible">
          <collapsible-pane title="{{faq.question}}" [collapsed]="faq.collapsed">
            <p class="faq-answer" [innerHTML]="faq.answer | safe: 'html'"></p>

            <div *ngIf="acslUser" class="faq-edit-link">
              <a href class="btn btn-default" [uiSref]="faqAddEditSref" [uiParams]="faqAddEditSrefParams" [target]="!onFaqPage ? '_blank' : '_self'"><i class="fa fa-pencil"></i> Edit</a>
            </div>

            <div *ngFor="let attachment of faq.attachments; trackById" class="faq-attachments">
              <i class="fa fa-paperclip"></i>&nbsp;<a [href]="attachment.value" target="_blank">{{ attachment.id }}</a>
            </div>
            <a uiSref="main.faq" [uiParams]="{faqId: faq.id}" target="_blank" *ngIf="faq.confluencePageHtml">More</a>
          </collapsible-pane>
        </div>
      </div>
    `,
  styleUrls: [
    './faq.scss',
    '../../main/faq/list/faq.scss'
  ],
  encapsulation: ViewEncapsulation.None
})
export class FaqItemComponent implements OnChanges {
  @Input() public faqId?:number;
  @Input() public collapsible:boolean;
  @Input() public faqCode?:string;
  @Input() public faqData?:IFaqItem;
  @Input() public noQuestion?:boolean;
  @Input() public noConfluenceText?:boolean;
  // indicate that faq accessed from page
  // if you change variable name here, should also change in faq.module.ts for param resolver
  @Input() public onFaqPage?: boolean;

  public faq:IFaqItem;
  public loading:boolean;
  public acslUser:boolean;
  private createNewFAQ:boolean;
  public hasPermissionToSendFAQ:boolean;

  constructor(
    private faqDataService:FaqDataService,
    private sendMailService: SendMailService,
    private translateService: TranslationService,
    private messageService: GaMessageService,
    private formattingService: FormattingService,
    private authService:AuthService
  ) {
    this.acslUser = authService.checkAcslUser();
    this.faq = <any>{};
  }

  public ngOnInit():void {
    this.start();
  }

  private start():void {
    if (this.faqData) {
      this.faq = util.copyAndExtend(this.faqData);
      this.updatePermissions(this.faq.securedDomain);
    } else {
      this.loadData();
    }
  }

  private loadData():void {
    if (this.faqId || this.faqCode) {
      this.loading = true;

      const faqPromise:Promise<IFaq> = this.faqCode ? this.faqDataService.getFaqByCode(this.faqCode) : this.faqDataService.getFaq(this.faqId);
      faqPromise
        .then((faq:IFaq) => {
          this.faq = this.processData(faq);
          this.updatePermissions(this.faq.securedDomain);
        })
        .catch(util.noop)
        .finally(() => {
          this.loading = false;
          // when the faq is not found when fetching by code -> allow to create a new faq
          this.createNewFAQ = this.faqCode && !this.faq.id;
        });
    }
  }

  private updatePermissions(securedDomain:string):void {
    this.authService.selectPermissions(securedDomain);
    this.hasPermissionToSendFAQ = this.authService.viewWrite(SEC_SEND_FAQ);
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes?.faqId?.currentValue) {
      this.faqId = parseInt(changes.faqId.currentValue);
    }

    if (changes?.collapsible?.currentValue) {
      this.collapsible = changes.collapsible.currentValue;
    }

    if (changes?.faqCode?.currentValue) {
      this.faqCode = changes.faqCode.currentValue;
    }

    if (changes?.faqdata?.currentValue) {
      this.faqData = changes.faqData.currentValue;
    }

    if (changes?.noQuestion?.currentValue) {
      this.noQuestion = changes.noQuestion.currentValue;
    }

    this.start();
  }

  public sendMail(): void {
    this.sendMailService.showSendMailDialog(
      this.translateService.instant('GA.BUTTONS.SEND_LABEL', { label: this.translateService.instant('GA.GENERAL.FAQ') }),
      this.faq?.question,
      undefined,
      this.onMailSend
    );
  }

  private onMailSend = (model: IMailRequest):Promise<any> => {
    return this.faqDataService.sendFaqs(model, undefined, undefined, undefined, this.faq.id)
      .then(() => {
        this.messageService.showFeedbackMessage(this.translateService.instant('GA.GENERAL.SENT', { label: this.translateService.instant('GA.GENERAL.FAQ') }));
      });
  };

  private processData(faq: IFaq): IFaqItem {
    const data: IFaqItem = util.copyAndExtend(faq);

    data.userList = this.formattingService.join(
      data.acslDisplay ? 'ACSL' : null,
      data.bpDisplay ? 'BP' : null,
      data.customerDisplay ? 'Customer' : null,
      data.vendorDisplay ? 'Vendor' : null,
      data.otherDisplay ? 'Other' : null,
      ', ' // separator
    );

    return data;
  }

  public get faqAddEditSref():string {
    return this.createNewFAQ ? STATE_FAQ_CREATE : STATE_FAQ_EDIT;
  }

  public get faqAddEditSrefParams():IObject {
    return this.createNewFAQ ? { code: this.faqCode } : { key: this.faqId || this.faqCode };
  }

}